import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { cn } from "@/lib/utils";
import { buildRangeQS } from "@/lib/drilldown";
import { InfoHint } from "./InfoHint";
import { CalendarDays, TrendingUp, TrendingDown, CalendarClock, Receipt, Wallet, Users, AlertCircle, ArrowUpRight, UserX } from "lucide-react";

const PRESETS = [
  { k: "today", label: "Today" },
  { k: "yesterday", label: "Yesterday" },
  { k: "this_week", label: "This Week" },
  { k: "last_week", label: "Last Week" },
  { k: "this_month", label: "This Month" },
  { k: "last_month", label: "Last Month" },
  { k: "custom", label: "Custom Range" },
];

const todayStr = () => new Date().toLocaleDateString("en-CA");

function Delta({ pct }) {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold",
      up ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700")}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(pct)}%
    </span>
  );
}

function Metric({ testid, icon: Icon, label, value, sub, delta, onClick, info }) {
  const clickable = Boolean(onClick);
  const Comp = clickable ? "button" : "div";
  return (
    <Comp
      type={clickable ? "button" : undefined}
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "group block w-full text-left rounded-2xl border border-slate-200 bg-white p-4 transition-all",
        clickable && "cursor-pointer hover:border-pawkt-primary/40 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-pawkt-primary/40 active:scale-[0.99]"
      )}
    >
      <div className="flex items-center gap-2 text-slate-400">
        <Icon className="h-4 w-4" />
        <span className="text-[11px] font-bold uppercase tracking-wide">{label}</span>
        {info}
        {clickable && <ArrowUpRight className="ml-auto h-3.5 w-3.5 text-slate-300 transition-colors group-hover:text-pawkt-primary-deep" />}
      </div>
      <div className="mt-2 flex items-end justify-between gap-2">
        <div className="font-display text-2xl font-extrabold tracking-tight text-slate-900" data-testid={`${testid}-value`}>{value}</div>
        {delta !== undefined && <Delta pct={delta} />}
      </div>
      {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
    </Comp>
  );
}

export function DashboardRange({ branch = "" }) {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  // Dashboard range is reflected in the URL so Back from a drill-down restores it.
  const urlPeriod = sp.get("period");
  const [period, setPeriodState] = useState(urlPeriod || "today");
  const [draftFrom, setDraftFrom] = useState(sp.get("start") || todayStr());
  const [draftTo, setDraftTo] = useState(sp.get("end") || todayStr());
  const [range, setRange] = useState({
    from: urlPeriod === "custom" ? (sp.get("start") || "") : "",
    to: urlPeriod === "custom" ? (sp.get("end") || "") : "",
  });
  const [error, setError] = useState("");

  // Keep component state in sync when the URL changes (e.g. browser Back).
  useEffect(() => {
    const p = sp.get("period") || "today";
    setPeriodState(p);
    if (p === "custom") {
      setRange({ from: sp.get("start") || "", to: sp.get("end") || "" });
      if (sp.get("start")) setDraftFrom(sp.get("start"));
      if (sp.get("end")) setDraftTo(sp.get("end"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const setPeriod = (k) => {
    setPeriodState(k);
    setError("");
    const next = new URLSearchParams(sp);
    next.set("period", k);
    if (k !== "custom") { next.delete("start"); next.delete("end"); }
    setSp(next, { replace: true });
  };

  const isCustom = period === "custom";
  const customReady = Boolean(isCustom && range.from && range.to);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["dashboard-summary", period, range.from, range.to, branch],
    queryFn: async () => {
      const params = { period };
      if (branch) params.branch = branch;
      if (isCustom) { params.start = range.from; params.end = range.to; }
      const q = new URLSearchParams(params).toString();
      return (await api.get(`/dashboard/summary?${q}`)).data;
    },
    enabled: !isCustom || customReady,
    keepPreviousData: true,
  });

  const applyCustom = () => {
    setError("");
    if (!draftFrom || !draftTo) { setError("Select both start and end dates."); return; }
    if (draftFrom > draftTo) { setError("Start date cannot be after end date."); return; }
    setRange({ from: draftFrom, to: draftTo });
    const next = new URLSearchParams(sp);
    next.set("period", "custom"); next.set("start", draftFrom); next.set("end", draftTo);
    setSp(next, { replace: true });
  };

  // Destination querystring carrying the active dashboard range.
  const qs = (extra) => buildRangeQS({ period, from: range.from, to: range.to }, extra);
  const go = (path) => () => navigate(`${path}${qs()}`);

  const d = data || {};
  const br = d.booking_revenue || {};
  const col = d.collections || {};
  const bk = d.bookings || {};
  const inv = d.invoices || {};
  const sl = d.sales || {};
  const rf = d.refunds || {};

  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5" data-testid="dashboard-range">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 text-slate-700">
          <CalendarDays className="h-4 w-4 text-pawkt-primary-deep" />
          <span className="text-sm font-bold">Business performance</span>
          {d.period?.label && <span className="text-xs text-slate-400">· {d.period.label}</span>}
        </div>
        <div className="inline-flex flex-wrap rounded-full bg-white border border-slate-200 p-1" data-testid="range-selector">
          {PRESETS.map((p) => (
            <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`range-${p.k}`}
              className={cn("rounded-full px-3 py-1.5 text-xs font-bold transition-colors",
                period === p.k ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900")}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {isCustom && (
        <div className="mt-3 flex flex-wrap items-end gap-3" data-testid="range-custom">
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 mb-1">From</label>
            <input type="date" value={draftFrom} max={todayStr()} onChange={(e) => setDraftFrom(e.target.value)}
              data-testid="range-from" className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 mb-1">To</label>
            <input type="date" value={draftTo} max={todayStr()} onChange={(e) => setDraftTo(e.target.value)}
              data-testid="range-to" className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white" />
          </div>
          <button onClick={applyCustom} data-testid="range-apply"
            className="rounded-lg bg-pawkt-primary px-4 py-2 text-sm font-bold text-white hover:bg-pawkt-primary-hover">Apply</button>
          {error && <span className="text-xs font-medium text-rose-600" data-testid="range-error">{error}</span>}
        </div>
      )}

      {isCustom && !customReady ? (
        <p className="mt-4 text-sm text-slate-400" data-testid="range-prompt">Pick a start and end date, then Apply to see metrics.</p>
      ) : isError ? (
        <p className="mt-4 text-sm text-rose-600" data-testid="range-fetch-error">Couldn't load metrics for this range.</p>
      ) : (
        <div className={cn("mt-4 grid grid-cols-2 lg:grid-cols-3 gap-3", isLoading && "opacity-60")} data-testid="range-metrics">
          <Metric testid="metric-sales" icon={Receipt} label="Net Sales (before GST)"
            value={inr(sl.current || 0)} delta={sl.change_pct} sub={`GST ${inr(sl.gst || 0)} separate · by sale date`} onClick={go("/invoices")} />
          <Metric testid="metric-booking-revenue" icon={CalendarClock} label="Delivered Service Value"
            value={inr(br.current || 0)} delta={br.change_pct} sub="bookings up to now (incl. GST)" onClick={go("/bookings")} />
          <Metric testid="metric-collections" icon={Wallet} label="Collections"
            value={inr(col.current || 0)} delta={col.change_pct} sub="money received (payment date)" onClick={go("/payments")} />
          <Metric testid="metric-refunds" icon={Wallet} label="Refunds"
            value={inr(rf.completed || 0)} sub={`net cash ${inr((d.net_cash || {}).current || 0)}${rf.pending ? ` · ${inr(rf.pending)} pending` : ""}`} onClick={go("/payments")} />
          <Metric testid="metric-membership-revenue" icon={Wallet} label="Membership Sales"
            value={inr((d.membership_revenue || {}).current || 0)} delta={(d.membership_revenue || {}).change_pct} sub="by sale date" onClick={go("/memberships")}
            info={<InfoHint as="span" testid="membership-revenue-info" label="Why is this based on sale date?" text="Based on sale date — shows memberships sold on the selected date." />} />
          <Metric testid="metric-bookings" icon={CalendarDays} label="Bookings"
            value={bk.total ?? 0} sub={`${bk.completed ?? 0} completed · ${bk.cancelled ?? 0} cancelled`} onClick={go("/bookings")} />
          <Metric testid="metric-invoices" icon={Receipt} label="Invoices"
            value={inv.count ?? 0} sub={`${inr(inv.total || 0)} invoiced`} onClick={go("/invoices")} />
          <Metric testid="metric-new-customers" icon={Users} label="New Customers"
            value={d.new_customers ?? 0} sub="joined in range" onClick={go("/customers")} />
          <Metric testid="metric-outstanding" icon={AlertCircle} label="Outstanding"
            value={inr(d.outstanding || 0)} sub="open balance (current)"
            onClick={() => navigate("/invoices?status=outstanding")} />
        </div>
      )}
      {d && ((d.unassigned_services || {}).count > 0 || (d.unassigned_services?.walkin_missing_performer?.lines || 0) > 0) && (
        <button type="button" onClick={() => navigate(d.unassigned_services.count > 0 ? `/bookings${qs({ unassigned: "true" })}` : "/staff-performance")}
          data-testid="unassigned-services-badge"
          className="mt-3 flex w-full items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left transition-colors hover:bg-amber-100/70">
          <div className="flex items-start gap-2 min-w-0">
            <UserX className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="min-w-0">
              <div className="text-sm font-bold text-amber-900">Unassigned Services</div>
              <div className="text-xs text-amber-700/90" data-testid="unassigned-services-detail">
                Completed bookings without a performer · invoiced value before GST
                {d.unassigned_services.not_yet_invoiced?.count ? ` · ${inr(d.unassigned_services.not_yet_invoiced.amount)} not yet invoiced` : ""}
                {d.unassigned_services.walkin_missing_performer?.lines ? ` · ${d.unassigned_services.walkin_missing_performer.lines} walk-in lines (${inr(d.unassigned_services.walkin_missing_performer.amount)}) missing performer` : ""}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm font-bold text-amber-900 tabular" data-testid="unassigned-services-value">
              {(d.unassigned_services.count)} service{d.unassigned_services.count === 1 ? "" : "s"} · {inr(d.unassigned_services.amount || 0)}
            </span>
            <ArrowUpRight className="h-4 w-4 text-amber-600" />
          </div>
        </button>
      )}
      {d && (d.membership_review || {}).count > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
             data-testid="membership-review-notice">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-800" data-testid="membership-review-message">
            {d.membership_review.message}
          </p>
        </div>
      )}
    </div>
  );
}
